import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TspBreadcrumb } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import notification from "../../../../helpers/notification";
import useTspApiResource, { tspErrorMessage } from "../../hooks/useTspApiResource";
import TspApiService from "../../api/tspApiService";
import { JOURNEY_RAIL_SECTIONS } from "../../constants/tspJourneyRail";
import useTspJourneyDetailData from "./useTspJourneyDetailData";
import useTspJourneyWorkflowActions from "./useTspJourneyWorkflowActions";
import useTspAddJourneyData from "./useTspAddJourneyData";
import { buildCityStatsByLocation, buildTimelineCities } from "./journeyDetailModel";
import JourneySectionNav from "./components/JourneySectionNav";
import JourneyCityTimeline from "./components/JourneyCityTimeline";
import JourneyOverviewCard from "./components/JourneyOverviewCard";
import JourneyRoutePlanCard from "./components/JourneyRoutePlanCard";
import JourneyStopDetails from "./components/JourneyStopDetails";
import JourneyPlaceAdder from "./builder/JourneyPlaceAdder";
import JourneyLeaderField from "./builder/JourneyLeaderField";
import useLivePlaceStore from "./builder/useLivePlaceStore";
import useJourneyPlacePerms from "./builder/useJourneyPlacePerms";
import useLazyBookCatalog from "./builder/useLazyBookCatalog";
import { chronologicalIndex, latestVisitByLocation, placeFromRouteCity, validatePlaceDate } from "./builder/journeyBuilderModel";
import { placeLabel, placeToRouteItem, samePlace, tidyName } from "../../utils/tspPlace";
import { leaderPayload } from "./journeyForm";

// "Update Journey" screen: the journey as stored on the server, its route as a
// timeline, and the actions the caller may take. A stop opened on the timeline
// shows its details with the same section tabs and forms as Add / Edit Journey
// (saved through the API as they are changed). Every action goes through the
// journey's workflow endpoints; the server decides what is allowed, and the
// screen re-loads afterwards so it always shows the real state.
export default function TspJourneyDetailPage() {
  const { journeyId } = useParams();
  // ?place=<journey place id>: open (and scroll to) that place of the journey.
  const [searchParams] = useSearchParams();
  const openPlaceId = searchParams.get("place");
  const [activeOpenPlaceId, setActiveOpenPlaceId] = useState(openPlaceId || null);
  const pendingAddedCity = useRef(null);
  const { journey, visits, reload } = useTspJourneyDetailData(journeyId);
  const options = useTspAddJourneyData();
  // The "142 books · ★ 4.8 rating" chip on each visited city — best-effort:
  // a caller without book-distribution/public-response view permission simply
  // gets no chip (the endpoint returns those sections as null), never an error.
  const stats = useTspApiResource(() => TspApiService.getJourneyStats(journeyId), [journeyId]);
  // Called unconditionally (it is a hook) — a no-op until `journey.data` arrives.
  const { quickActions, handleAction, dialogs } = useTspJourneyWorkflowActions(journey.data, reload);
  // What each stop's sections may show and record (the server checks again).
  const { perms: basePerms, views } = useJourneyPlacePerms(journey.data);
  const bookCatalog = useLazyBookCatalog();

  const data = journey.data;

  useEffect(() => {
    if (openPlaceId) setActiveOpenPlaceId(openPlaceId);
  }, [openPlaceId]);

  useEffect(() => {
    if (pendingAddedCity.current && data?.route && data.route.length > 0) {
      const { beforeIds } = pendingAddedCity.current;
      const newlyAdded = data.route.find((r) => !beforeIds.has(String(r.id)));
      if (newlyAdded) {
        setActiveOpenPlaceId(String(newlyAdded.id));
        pendingAddedCity.current = null;
      }
    }
  }, [data?.route]);

  const perms = useMemo(
    () => ({
      ...basePerms,
      visits: data?.status !== "CANCELLED",
    }),
    [basePerms, data?.status]
  );
  const savedLeader = useMemo(
    () => ({ name: data?.leader?.name || data?.team_leader_name || "", userId: data?.leader?.id ? String(data.leader.id) : "" }),
    [data?.leader, data?.team_leader_name]
  );
  const [leaderDraft, setLeaderDraft] = useState(null);
  const [routeBusy, setRouteBusy] = useState(false);
  const [routeOverrides, setRouteOverrides] = useState({});
  const leader = leaderDraft || savedLeader;

  useEffect(() => {
    setRouteOverrides({});
  }, [data?.route]);

  if (journey.error) return <TspLoadError error={journey.error} onRetry={journey.reload} what="this journey" />;
  if (!journey.data) return <div className="tsp-page tsp-dashboard-loading">Loading journey…</div>;

  const pageRoutes = { journey: `/tsp/journeys/${journeyId}`, stats: `/tsp/stats/${journeyId}` };
  const statsByLocation = buildCityStatsByLocation(stats.data?.cities);
  const timelineCities = buildTimelineCities(data.route, visits.data || [], statsByLocation, activeOpenPlaceId, routeOverrides);
  const summary = {
    id: data.id,
    // As the City Visits screen names it: each year's journey is its own record.
    name: data.year ? `${data.journey_name} (${data.year})` : data.journey_name,
    startDate: data.start_date || "",
    endDate: data.end_date || "",
    status: data.status,
    // "Visit Now" only where the server would accept a visit (permission + in progress).
    canRecordVisit: Boolean(data.capabilities?.record_visit),
    // "Finish Journey" on the last stop (running, permitted, last stop visited).
    canFinish: Boolean(data.capabilities?.finish && data.status === "IN_PROGRESS"),
    // Counted from the same per-stop states the stop cards show: the stop the
    // team is at now is "LIVE - In Progress", neither Completed nor Upcoming.
    // (execution.visited_city_count includes that current stop.)
    completedCount: timelineCities.filter((city) => city.status === "completed").length,
    upcomingCount: timelineCities.filter((city) => city.status === "not_visited").length,
  };
  const range = { startDate: data.start_date || "", endDate: data.end_date || "" };
  const placesById = new Map(
    (data.route || []).map((routeCity) => {
      const place = placeFromRouteCity(routeCity);
      if (routeOverrides[String(routeCity.id)]) {
        place.plannedDate = routeOverrides[String(routeCity.id)];
      }
      return [String(routeCity.id), place];
    })
  );
  const visitsByLocation = latestVisitByLocation(visits.data);
  const reloadAll = () => {
    reload();
    stats.reload();
  };
  const renderDetails = (city) => {
    const place = placesById.get(String(city.id));
    if (!place) return null;
    return (
      <JourneyStopDetails
        place={place}
        perms={perms}
        range={range}
        bookCatalog={bookCatalog}
        usePlaceStore={(current, load) =>
          // eslint-disable-next-line react-hooks/rules-of-hooks -- always this one hook for the stop's whole life
          useLivePlaceStore({ journeyId: data.id, place: current, visitRecord: visitsByLocation.get(current.locationId) || null, load, perms: views, range, onVisitChanged: reloadAll, onSaved: stats.reload })
        }
      />
    );
  };
  const visitsNotice = visits.error ? "Visit details could not be loaded for your role, so cities are listed without them." : null;

  const canEditLeader = data.status !== "CANCELLED";
  const canEditRoute = data.status !== "CANCELLED";

  const handleLeaderChange = async (newLeader) => {
    setLeaderDraft(newLeader);
    if (canEditLeader) {
      try {
        await TspApiService.updateJourney(data.id, leaderPayload({ leaderName: newLeader.name, leaderUserId: newLeader.userId }));
        notification("success", "Team Leader updated.");
        reload();
      } catch (error) {
        notification("error", tspErrorMessage(error, "Could not update Team Leader."));
      }
    }
  };

  const handleAddPlace = async (place, date) => {
    if (!place || !place.stateId || !place.districtId || !tidyName(place.city)) {
      return { ok: false, message: "Select a state, a district and a taluka / town / municipality first." };
    }
    const problem = validatePlaceDate(date);
    if (problem) return { ok: false, message: problem };

    const places = (data.route || []).map(placeFromRouteCity);
    const duplicate = places.find((existing) => samePlace(existing, { ...place, city: tidyName(place.city) }));
    if (duplicate) return { ok: false, message: `${placeLabel(duplicate)} is already in this journey.` };

    const position = chronologicalIndex(places, date) + 1;
    const beforeIds = new Set((data.route || []).map((r) => String(r.id)));
    pendingAddedCity.current = { beforeIds };
    const hasCoords = Number.isFinite(place.latitude) && Number.isFinite(place.longitude);
    try {
      const response = await TspApiService.addRouteCity(
        data.id,
        {
          ...placeToRouteItem({ ...place, city: tidyName(place.city) }),
          planned_date: date,
          ...(hasCoords ? { latitude: place.latitude, longitude: place.longitude } : {}),
          ...(place.areas && place.areas.length ? { areas: place.areas } : {}),
        },
        position
      );
      notification("success", "Place added to journey.");
      const citiesList = response?.data?.cities || response?.data?.data?.cities || response?.cities || [];
      const added = citiesList.find((c) => !beforeIds.has(String(c.id)));
      if (added) {
        setActiveOpenPlaceId(String(added.id));
        pendingAddedCity.current = null;
      }
      reloadAll();
      return { ok: true };
    } catch (error) {
      pendingAddedCity.current = null;
      const message = tspErrorMessage(error, "Could not add the place.");
      notification("error", message);
      return { ok: false, message };
    }
  };

  const handleRemovePlace = async (city) => {
    if (!city || !city.id) return;
    if (typeof window !== "undefined" && window.confirm && !window.confirm(`Remove ${city.name || "this place"} from this journey?`)) {
      return;
    }
    setRouteBusy(true);
    try {
      await TspApiService.removeRouteCity(data.id, city.id);
      notification("success", `${city.name || "Place"} removed from journey.`);
      reloadAll();
    } catch (error) {
      notification("error", tspErrorMessage(error, "Could not remove the place."));
    } finally {
      setRouteBusy(false);
    }
  };

  const handleMovePlace = async (index, direction) => {
    const route = data.route || [];
    const target = index + direction;
    if (target < 0 || target >= route.length) return;
    const ids = route.map((item) => item.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setRouteBusy(true);
    try {
      await TspApiService.reorderRoute(data.id, ids);
      notification("success", "Route order updated.");
      reloadAll();
    } catch (error) {
      notification("error", tspErrorMessage(error, "Could not reorder the route."));
    } finally {
      setRouteBusy(false);
    }
  };

  const handleDateChange = async (city, date) => {
    if (!city || !city.id) return { ok: false, message: "Invalid place." };
    const problem = validatePlaceDate(date);
    if (problem) return { ok: false, message: problem };
    setRouteBusy(true);
    try {
      await TspApiService.updateRouteCity(data.id, city.id, { planned_date: date });
      setRouteOverrides((prev) => ({ ...prev, [String(city.id)]: date }));
      notification("success", "Visit date updated.");
      reloadAll();
      return { ok: true };
    } catch (error) {
      const message = tspErrorMessage(error, "Could not update the visit date.");
      notification("error", message);
      return { ok: false, message };
    } finally {
      setRouteBusy(false);
    }
  };

  // "Update Location" popup -> Save: stores the stop's fetched point.
  const handleSaveLocation = async (city, point) => {
    if (!city || !city.id) return { ok: false, message: "Invalid place." };
    setRouteBusy(true);
    try {
      await TspApiService.updateRouteCityLocation(data.id, city.id, point);
      notification("success", `Location updated for ${city.name}.`);
      reloadAll();
      return { ok: true };
    } catch (error) {
      return { ok: false, message: tspErrorMessage(error, "Could not save the location.") };
    } finally {
      setRouteBusy(false);
    }
  };

  const handlePlaceStatusChange = async (city, newStatus) => {
    if (!city) return;
    setRouteBusy(true);
    try {
      await TspApiService.updatePlaceStatus(journeyId, {
        location_id: Number(city.locationId),
        status: newStatus,
      });
      notification("success", `Status updated to ${newStatus === "live" ? "In Progress" : newStatus === "completed" ? "Completed" : "Upcoming"}.`);
      reloadAll();
    } catch (err) {
      notification("error", tspErrorMessage(err, "Could not update place status."));
    } finally {
      setRouteBusy(false);
    }
  };

  return (
    <div className="tsp-page tsp-journey-detail-page">
      <div className="tsp-journey-detail-layout">
        <div className="tsp-journey-detail-main">
          <TspBreadcrumb items={["Dashboard", "Journeys", data.journey_name]} />
          <span id="overview" className="tsp-anchor" />
          <JourneyOverviewCard journey={data} />
          <JourneyRoutePlanCard places={timelineCities} />
          <span id="execution-workflow" className="tsp-anchor" />
          <div id="city-activities">
            <JourneyCityTimeline
              journey={summary}
              cities={timelineCities}
              renderDetails={renderDetails}
              visitsNotice={visitsNotice}
              canEditRoute={canEditRoute}
              onMovePlace={handleMovePlace}
              onRemovePlace={handleRemovePlace}
              onDateChange={handleDateChange}
              onSaveLocation={handleSaveLocation}
              onStatusChange={handlePlaceStatusChange}
              disabled={routeBusy}
              onVisitRecorded={reloadAll}
              onFinished={() => {
                notification("success", "Journey completed.");
                reload();
                stats.reload();
              }}
            />
          </div>


          {canEditRoute && (
            <div id="add-journey-place">
              <JourneyPlaceAdder
                title="Add Journey Place"
                states={options.data?.states || []}
                onAdd={handleAddPlace}
                placeCount={timelineCities.length}
                showPicker={canEditRoute}
                coordinatesReadOnly
                leader={<JourneyLeaderField value={leader} onChange={handleLeaderChange} disabled={!canEditLeader} />}
              />
            </div>
          )}
        </div>

        <JourneySectionNav sections={JOURNEY_RAIL_SECTIONS} quickActions={quickActions} currentPage="journey" pageRoutes={pageRoutes} onAction={handleAction} />
      </div>

      {dialogs}
      <ToastContainer />
    </div>
  );
}
